import { DateOnlyInput } from "../components/DateOnlyInput";
import { alphaMutationRejected } from "../alpha-mutation";
import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router";
import { api, authenticatedFetch } from "../api";
import { ErrorState, Loading } from "../ui/State";
export function ComplaintsAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building");
  const [list, setList] = useState<any>(),
    [selected, setSelected] = useState(""),
    [error, setError] = useState<unknown>();
  useEffect(() => {
    setList(undefined);
    setSelected("");
    let active = true;
    if (building)
      api("/api/alpha/complaints?building=" + building)
        .then((r) => {
          if (active) setList(r);
        })
        .catch((e) => {
          if (active) setError(e);
        });
    return () => {
      active = false;
    };
  }, [building]);
  return (
    <>
      <h1>Complaint reviews</h1>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {!building ? (
        <p>Choose a building.</p>
      ) : !list ? (
        <Loading />
      ) : (
        <>
          <label>
            Correspondence case
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">Choose a case</option>
              {list.cases.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.title} · {c.complaint_stage}
                </option>
              ))}
            </select>
          </label>
          {!list.cases.length&&<p className="empty">No complaint cases are recorded for this building. Review incoming correspondence in Communications first.</p>}
          {selected && <Complaint key={selected} id={selected} />}
        </>
      )}
    </>
  );
}
function Complaint({ id }: { id: string }) {
  const root = "/api/alpha/complaints/" + id,
    [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState("");
  const pending = useRef<{ path: string; body: any } | null>(null);
  const load = () => api(root).then(setModel);
  useEffect(() => {
    void load().catch(setError);
  }, [id]);
  async function save(path: string, body: any) {
    pending.current ??= {
      path,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    setBusy(true);
    try {
      await api(root + pending.current.path, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      setNotice("Saved.");
      setError(undefined);
      await load();
    } catch (e) {
      if (alphaMutationRejected(e)) pending.current = null;
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function download(pack: string) {
    try {
      const r = await authenticatedFetch(root + "/packs/" + pack);
      if (!r.ok) throw Error("Pack unavailable.");
      const u = URL.createObjectURL(await r.blob()),
        a = document.createElement("a");
      a.href = u;
      a.download = "complaint-pack.html";
      a.click();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    } catch (e) {
      setError(e);
    }
  }
  const stages: Record<string, string[]> = {
    "Not a complaint": ["Stage 1"],
    "Stage 1": ["Stage 1", "Stage 2", "Closed"],
    "Stage 2": ["Stage 2", "Ombudsman", "Closed"],
    Ombudsman: ["Ombudsman", "Closed"],
    Closed: [],
  };
  return (
    <section className="panel">
      {Boolean(error) && <ErrorState error={error} />}
      <p role="status">{notice}</p>
      {pending.current && !busy && (
        <button onClick={() => void save("", {})}>Retry the same save</button>
      )}
      {!model ? (
        <Loading />
      ) : (
        <>
          <h2>{model.case.title}</h2>
          <p>
            {model.case.complaint_stage} · {model.case.status}
          </p>
          {model.case.complaint_stage !== "Closed" && (
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                void save("/review", {
                  expectedStage: model.case.complaint_stage,
                  stage: f.get("stage"),
                  summary: f.get("summary"),
                  ...(f.get("due") ? { dueDate: f.get("due") } : {}),
                });
              }}
            >
              <fieldset disabled={busy || !!pending.current}>
                <legend>Record review</legend>
                <label>
                  Stage
                  <select name="stage">
                    {stages[model.case.complaint_stage]?.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Outcome and next steps
                  <textarea
                    name="summary"
                    required
                    minLength={10}
                    maxLength={5000}
                  />
                </label>
                <label>
                  Next review date
                  <DateOnlyInput name="due" />
                </label>
                <button>Save review</button>
              </fieldset>
            </form>
          )}
          <h3>Agreed remedies</h3>
          {model.remedies.map((a: any) => (
            <p key={a.id}>
              <Link to={"/work/actions/" + a.id}>{a.title}</Link> · {a.status} ·
              Due {String(a.due_at).slice(0, 10)}
            </p>
          ))}
          {!["Closed", "Not a complaint"].includes(
            model.case.complaint_stage,
          ) && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                void save("/remedies", {
                  title: f.get("title"),
                  dueDate: f.get("due"),
                });
              }}
            >
              <fieldset disabled={busy || !!pending.current}>
                <legend>Add remedy</legend>
                <label>
                  What needs to happen
                  <input name="title" required minLength={5} maxLength={250} />
                </label>
                <label>
                  Due date
                  <DateOnlyInput name="due" required />
                </label>
                <button>Add remedy</button>
              </fieldset>
            </form>
          )}
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              void save("/packs", {
                summary: f.get("summary"),
                documentIds: f.getAll("documents"),
              });
            }}
          >
            <fieldset disabled={busy || !!pending.current}>
              <legend>Resolution pack</legend>
              <label>
                Reviewed summary
                <textarea
                  name="summary"
                  required
                  minLength={10}
                  maxLength={10000}
                />
              </label>
              {model.documents.map((d: any) => (
                <label key={d.id}>
                  <input type="checkbox" name="documents" value={d.id} />
                  {d.title}
                </label>
              ))}
              <p>
                Retains the review chronology and remedies as they stand now.
                Review before sharing externally.
              </p>
              <button>Retain resolution pack</button>
            </fieldset>
          </form>
          <h3>History</h3>
          {model.history.map((h: any) => (
            <article key={h.id}>
              <h4>
                {h.event_type} · {String(h.created_at).slice(0, 10)}
              </h4>
              <p>{h.detail.summary ?? h.detail.title}</p>
              {h.event_type === "Complaint pack retained" && (
                <button onClick={() => void download(h.id)}>
                  Download pack
                </button>
              )}
            </article>
          ))}
        </>
      )}
    </section>
  );
}
